// src/app/auth/token.interceptor.ts
import {Injectable, Injector} from "@angular/core";
import {HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest} from "@angular/common/http";
import {AuthService} from "./services";
import {EMPTY, Observable, throwError} from "rxjs";
import {Router} from "@angular/router";
import {HelperService} from "./services/helperService";
import {Ng4LoadingSpinnerService} from "ng4-loading-spinner";
import {catchError, flatMap, map} from "rxjs/operators";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";


@Injectable()
export class TokenInterceptor implements HttpInterceptor {

	private auth_service: AuthService;
	private router: Router;
	private http_requests: HttpRequest<any>[] = [];


	/**
	 * @param {Injector} injector
	 * @param {HelperService} helper_service
	 * @param {Ng4LoadingSpinnerService} spinner_service
	 * @param toastr_service
	 * @param translate_service
	 */
	constructor
	(
		private injector: Injector,
		private helper_service: HelperService,
		private spinner_service: Ng4LoadingSpinnerService,
		private toastr_service: ToastrService,
		private translate_service: TranslateService
	) {
	}


	/**
	 * @param {HttpRequest} http_request
	 * @param {HttpHandler} http_handler
	 * @return {Observable<HttpEvent>}
	 */
	intercept(http_request: HttpRequest<any>, http_handler: HttpHandler): Observable<HttpEvent<any>> {
		// get access token from locale storage
		this.auth_service = this.injector.get(AuthService);
		let token = this.auth_service.getToken("access_token");

		// config call
		if (http_request.url.indexOf("/gauntlet/") !== -1) {

			// generate timestamp to make a URL unique (no browser cache)
			let timestamp = +new Date();

			http_request = http_request.clone
			({
				url: http_request.url + "?tsp=" + timestamp
			});

		}
		// api call and access token exists
		else if (http_request.url.indexOf("/api/") !== -1 && token != null) {
			let client = this.helper_service.getFromLocalstorage('client');

			this.helper_service.log
			(
				{class: 'TokenInterceptor', function: 'intercept'},
				"token interceptor: this is my current client, read from local storage",
				client
			);

			// Hole GET-Parameter um sie anzureichern
			let http_request_params = http_request.params;

			// Es gibt querries, die per config übergeben werden und bereits GET-Parameter in der URL übergeben -
			// damit diese auch gehasht werden, müssen sie hier herausgefiltert werden
			let url_queries = http_request.url.split('?'),
				url_without_params = http_request.url;

			if (url_queries.length > 1) {
				let parts = url_queries[1].split('&');
				if (parts.length > 1) {
					parts.forEach((entry) => {
						let query_parts = entry.split('=');
						http_request_params = http_request_params.append(query_parts[0], query_parts[1]);
					});
				} else {
					let query_parts = parts[0].split('=');
					http_request_params = http_request_params.append(query_parts[0], query_parts[1]);
				}

				url_without_params = url_queries[0]
			}

			// Hänge an jeden Request Netzwerk und Application an
			if (client != null) {
				// don't overwrite existing parameters
				if (http_request_params.get("application") === null) {
					http_request_params = http_request_params.append("application", client.id);
				}

				if (http_request_params.get("network") === null) {
					http_request_params = http_request_params.append("network", client.network);
				}

			}

			// Sortierte keys der GET-Parameter fuers hashen
			let keys = http_request_params.keys().sort();

			// P2SD-11271:
			// Die Parameter fuers hashen muessen per Hand zusammengestellt werden, da die Angular-Funktion OBJ_params.toString() Sonderzeichen encoded,
			// in der Api aber von nicht encodeden Parametern ausgegangen wird.
			let params_to_hash = "";

			keys.forEach((key) => {

				if (params_to_hash != "") {
					params_to_hash = params_to_hash + "&";
				}

				params_to_hash = params_to_hash + key + "=" + http_request_params.get(key);
			});

			// save body without hash in case the hash has to be generated with fresh access token - otherwise the body including the hash would be hashed again
			let http_request_body = http_request.body;

			const http_request_hashed = this.generateRequestWithHash(
				http_request, http_request_params, http_request_body, url_without_params, token, params_to_hash
			);

			// Sende angereichertern Request ab und reagiere auf eventuelle Fehler bzw. Statuscodes != 200
			return http_handler.handle(http_request_hashed).pipe(
				map((response: any) => {
					// Der Inhalt wurde bereits von jemand anderem geupdated, update nicht moeglich
					if (response.status === 205) {

						this.toastr_service.error(
							this.translate_service.instant('oError.sContentChanged'),
							this.translate_service.instant('oError.sContentChangedTitle'),
							{
								disableTimeOut: true,
								closeButton: true,
								tapToDismiss: false,
								enableHtml: true
							});
					}

					return response;
				}),
				catchError((response: HttpErrorResponse) => {

					this.helper_service.log(
						{class: 'TokenInterceptor', function: 'intercept'},
						response
					);

					let error = response.error;
					switch (response.status) {
						// unauthorized or forbidden
						case 401:
						case 403:
							return this.auth_service.refreshToken().pipe(flatMap(() => {
								// get access token from locale storage
								let token = this.auth_service.getToken("access_token");

								// Generiere neue Anfrage mit neu gehashten Parametern und Body
								const http_request_new = this.generateRequestWithHash(
									http_request_hashed, http_request_params, http_request_body,
									url_without_params, token, params_to_hash
								);

								return http_handler.handle(http_request_new)
							}))

						case 400:
							if (error && error.title && error.title.toLowerCase() === "invalid_grant") {
								this.helper_service.log(
									{
										class: 'TokenInterceptor',
										function: 'intercept'
									},
									"Response token invalid"
								);

								// Logge Nutzer aus
								this.router = this.injector.get(Router);
								this.router.navigate([
									"/login",
									{STR_error: "oError.sRefreshToken"}
								]).then(r => {});

								return EMPTY;
							} else {
								// Gib Fehler weiter
								return throwError(response);
							}

						// Duplicate Content
						case 409:
							this.toastr_service.error(
								this.translate_service.instant('oError.sContentChanged'),
								this.translate_service.instant('oError.sContentChangedTitle'),
								{
									disableTimeOut: true,
									closeButton: true,
									tapToDismiss: false,
									enableHtml: true
								});
							return throwError(response);

						case 500:
							if (error.type === "custom") {
								this.toastr_service.error(
									this.translate_service.instant(error.title),
									null,
									{
										disableTimeOut: true,
										closeButton: true,
										tapToDismiss: false,
										enableHtml: true
									});
								return;
							}
							return throwError(response);

						default:
							// Gib Fehler weiter
							return throwError(response);
					}

				})
			);

		}

		return http_handler.handle(http_request);

	}


	/**
	 * @param {HttpRequest} http_request
	 */
	removeHttpRequest(http_request: HttpRequest<any>) {

		// get url without get parameters
		let url = http_request.url.split('?')[0],
			// get index in array
			idx = this.http_requests.findIndex((item) => item.url.split('?')[0] === url);

		this.helper_service.log(
			{class: 'TokenInterceptor', function: 'removeHttpRequest'},
			{
				url: url,
				idx: idx,
				requests: this.http_requests,
				router_spinner: this.helper_service.router_spinner
			}
		);

		if (idx >= 0) {
			// remove the request from array
			this.http_requests.splice(idx, 1);
		}

		console.log(
			this.http_requests.length,
			this.helper_service.router_spinner,
			'this.ARR_http_requests.length === 0 && this.OBJ_helper_service.BOO_router_spinner === false',
			this.http_requests.length === 0 && this.helper_service.router_spinner === false
		);

		// hide spinner
		if (this.http_requests.length === 0 && this.helper_service.router_spinner === false) {
			this.spinner_service.hide();
		}


	}


	/**
	 * @param http_request
	 * @param url
	 * @param body
	 * @param token
	 * @param http_params
	 * @param params_to_hash
	 */
	generateRequestWithHash(
		http_request: HttpRequest<any>, http_params: HttpParams, body, url: string, token: string, params_to_hash: string
	):  HttpRequest<any> {

		// Bei einem Post-Request wird auch der Body gehasht und zusammen mit Hash weitergeschickt
		if (body !== null && !http_request.url.includes('oauth')) {

			body = this.helper_service.hashBody(body, token);
		}

		// Haenge Hash an Parameter an
		http_params = http_params.append("hash", this.helper_service.generateChecksum(params_to_hash, token));

		return http_request.clone
		({
			setHeaders: {
				Authorization: "Bearer " + token
			},
			params: http_params,
			url: url,
			body: body
		});
	}
}
